/**
 * 副作用层（Effect Layer）
 *
 * 职责：
 * 1. 监听 FSM 状态变化
 * 2. 在状态进入/退出时执行副作用
 * 3. 与 FSM 解耦，FSM 保持纯函数
 *
 * 设计原则：
 * - 副作用只在状态变化时触发
 * - 使用 onEnter / onExit 模式
 * - 幂等性由状态机保证
 */

import { VideoState, AudioState } from './RemoteMediaFSM.js'
import { createLogger } from '@/utils/logger'

const logger = createLogger('EffectLayer')

/**
 * 检查是否进入某个状态
 * @param {string} prevState - 前一个状态
 * @param {string} nextState - 下一个状态
 * @param {string} targetState - 目标状态
 * @returns {boolean}
 */
function isEntering(prevState, nextState, targetState) {
  return prevState !== targetState && nextState === targetState
}

/**
 * 检查是否退出某个状态
 * @param {string} prevState - 前一个状态
 * @param {string} nextState - 下一个状态
 * @param {string} targetState - 目标状态
 * @returns {boolean}
 */
function isExiting(prevState, nextState, targetState) {
  return prevState === targetState && nextState !== targetState
}

/**
 * 创建副作用层
 * @param {Object} options - 配置选项
 * @param {Function} options.onPlayVideo - 播放视频回调
 * @param {Function} options.onStopVideo - 停止视频回调
 * @param {Function} options.onPlayAudio - 播放音频回调
 * @param {Function} options.onStopAudio - 停止音频回调
 * @param {Function} options.onReportFirstFrame - 上报首帧回调
 * @param {Function} options.onEventBusEmit - 发射事件总线事件
 * @returns {Function} 状态变化处理函数
 */
export function createEffectLayer({
  onPlayVideo,
  onStopVideo,
  onPlayAudio,
  onStopAudio,
  onReportFirstFrame,
  onEventBusEmit,
}) {
  /**
   * 状态变化处理函数
   * @param {Object} result - FSM dispatch 返回的结果
   * @param {Object} result.prevState - 前一个状态
   * @param {Object} result.nextState - 下一个状态
   * @param {Array} result.transitions - 状态迁移列表
   * @param {Object} result.event - 触发的事件
   */
  return function handleStateChange(result) {
    const { prevState, nextState, transitions, event } = result

    // 没有状态变化，跳过
    if (transitions.length === 0) {
      return
    }

    const uid = nextState.uid

    // ========== 视频副作用 ==========

    // 当视频 published 且 view mounted 时，立即播放
    // 这会触发 first-frame-decoded 事件，然后状态转为 PLAYING
    if (
      (event.type === 'VIDEO_PUBLISHED' || event.type === 'VIEW_MOUNTED') &&
      nextState.video.state === VideoState.PUBLISHED &&
      nextState.view.state === 'mounted' &&
      nextState.video.track &&
      nextState.view.element
    ) {
      logger.log(`[${uid}] Track and view ready, starting playback`)
      onPlayVideo?.(uid, nextState.video.track, nextState.view.element)
    }

    // 进入 PLAYING 状态 → 记录日志（播放已在上面触发）
    if (isEntering(prevState.video.state, nextState.video.state, VideoState.PLAYING)) {
      logger.log(`[${uid}] Entered video.playing state`)
    }

    // 退出 PLAYING 状态 → 停止视频
    if (isExiting(prevState.video.state, nextState.video.state, VideoState.PLAYING)) {
      logger.log(`[${uid}] Exiting video.playing state`)

      if (prevState.video.track) {
        onStopVideo?.(uid, prevState.video.track)
      }
    }

    // 进入 FROZEN 状态 → 记录卡顿
    if (isEntering(prevState.video.state, nextState.video.state, VideoState.FROZEN)) {
      logger.warn(`[${uid}] Video frozen`)
      // TODO: 上报卡顿事件
    }

    // 视频首帧解码 → 上报（只在第一次上报）
    if (
      event.type === 'VIDEO_FIRST_FRAME' &&
      nextState.video.firstFrameTime !== null &&
      prevState.video.firstFrameTime === null // ← 只在首次上报
    ) {
      logger.log(`[${uid}] First frame decoded: ${nextState.video.firstFrameTime}s`)
      onReportFirstFrame?.(uid, nextState.video.firstFrameTime, 'video')
    }

    // ========== 音频副作用 ==========

    // 进入 PLAYING 状态 → 播放音频
    if (isEntering(prevState.audio.state, nextState.audio.state, AudioState.PLAYING)) {
      logger.log(`[${uid}] Entering audio.playing state`)

      if (nextState.audio.track) {
        onPlayAudio?.(uid, nextState.audio.track)
      }
    }

    // 退出 PLAYING 状态 → 停止音频
    if (isExiting(prevState.audio.state, nextState.audio.state, AudioState.PLAYING)) {
      logger.log(`[${uid}] Exiting audio.playing state`)

      if (prevState.audio.track) {
        onStopAudio?.(uid, prevState.audio.track)
      }
    }

    // ========== EventBus 事件发射 ==========

    // 为了保持向后兼容，发射对应的 EventBus 事件
    transitions.forEach(({ dimension, from, to }) => {
      onEventBusEmit?.({
        type: 'STATE_TRANSITION',
        payload: {
          uid,
          dimension,
          from,
          to,
          prevState,
          nextState,
        },
      })
    })
  }
}

/**
 * 快捷函数：创建视频播放副作用
 * @param {Function} playFn - 播放函数
 * @returns {Function}
 */
export function createVideoPlayEffect(playFn) {
  return (result) => {
    const { prevState, nextState } = result

    if (isEntering(prevState.video.state, nextState.video.state, VideoState.PLAYING)) {
      const { uid } = nextState
      const { track } = nextState.video
      const { element } = nextState.view

      if (track && element) {
        playFn(uid, track, element)
      }
    }
  }
}

/**
 * 快捷函数：创建视频停止副作用
 * @param {Function} stopFn - 停止函数
 * @returns {Function}
 */
export function createVideoStopEffect(stopFn) {
  return (result) => {
    const { prevState, nextState } = result

    if (isExiting(prevState.video.state, nextState.video.state, VideoState.PLAYING)) {
      const { uid } = nextState
      const { track } = prevState.video

      if (track) {
        stopFn(uid, track)
      }
    }
  }
}

/**
 * 组合多个副作用
 * @param  {...Function} effects - 副作用函数列表
 * @returns {Function}
 */
export function composeEffects(...effects) {
  return (result) => {
    effects.forEach(effect => {
      try {
        effect(result)
      } catch (error) {
        logger.error('Effect error:', error)
      }
    })
  }
}
